import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { Check, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { fadeUp } from "../../lib/motion";
import { useLanguage } from "../../lib/i18n";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const empty = { name: "", email: "", phone: "", event_date: "", venue: "", guests: "", ceremony_location: "", reception_location: "", wedding_style: "", menu: "", dietary: "", budget: "", additional_info: "", type: "wedding", services: [] };

export const WeddingForm = () => {
  const [form, setForm] = useState(empty);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const { t } = useLanguage();
  const serviceOptions = t("wform.services");
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const toggle = (s) => setForm((f) => ({ ...f, services: f.services.includes(s) ? f.services.filter((x) => x !== s) : [...f.services, s] }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim()) { toast.error(t("toast.needName")); return; }
    setLoading(true);
    try { await axios.post(`${API}/bookings`, form); setDone(true); toast.success(t("toast.okWedding")); }
    catch (err) { console.error(err); toast.error(t("toast.err")); }
    finally { setLoading(false); }
  };

  return (
    <section id="wedding-enquiry" data-testid="wedding-form-section" className="tiled relative py-8 md:py-10 bg-[--bg-2]">
      <div className="max-w-[1100px] mx-auto px-5 md:px-10">
        <motion.div variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-100px" }} className="max-w-2xl mb-12">
          <p className="font-mono uppercase tracking-[0.3em] text-[11px] text-[--gold] mb-6">{t("wform.eyebrow")}</p>
          <h2 className="font-display font-light tracking-tight leading-[1.05] text-3xl sm:text-4xl md:text-5xl text-[--text]">{t("wform.h2a")} <span className="italic text-[--gold]">{t("wform.h2b")}</span></h2>
        </motion.div>

        <AnimatePresence mode="wait">
          {done ? (
            <motion.div key="s" data-testid="wedding-success" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="border border-white/10 rounded-2xl p-12 bg-[--surface] flex flex-col items-start gap-6">
              <div className="w-14 h-14 rounded-full bg-[--gold] flex items-center justify-center"><Check className="text-[#14180E]" strokeWidth={1.5} /></div>
              <h3 className="font-display text-3xl text-[--text]">{t("wform.successH")}</h3>
              <p className="text-[--text-2] max-w-md">{t("quote.thanks")} {form.name.split(" ")[0]}. {t("wform.successP")}</p>
            </motion.div>
          ) : (
            <motion.form key="f" data-testid="wedding-form" onSubmit={submit} exit={{ opacity: 0 }} className="bg-[--surface] border border-white/10 rounded-2xl p-8 md:p-10 grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-7">
              <F l={t("wform.l.name")}><input data-testid="w-name" className="editorial-input mt-2" value={form.name} onChange={set("name")} placeholder={t("wform.p.name")} /></F>
              <F l={t("wform.l.email")}><input data-testid="w-email" type="email" className="editorial-input mt-2" value={form.email} onChange={set("email")} placeholder={t("wform.p.email")} /></F>
              <F l={t("wform.l.phone")}><input className="editorial-input mt-2" value={form.phone} onChange={set("phone")} placeholder={t("wform.p.optional")} /></F>
              <F l={t("wform.l.date")}><input data-testid="w-date" type="date" className="editorial-input mt-2" value={form.event_date} onChange={set("event_date")} /></F>
              <F l={t("wform.l.venue")}><input className="editorial-input mt-2" value={form.venue} onChange={set("venue")} placeholder={t("wform.p.venue")} /></F>
              <F l={t("wform.l.guests")}><input className="editorial-input mt-2" value={form.guests} onChange={set("guests")} placeholder={t("wform.p.guests")} /></F>
              <F l={t("wform.l.ceremony")}><input className="editorial-input mt-2" value={form.ceremony_location} onChange={set("ceremony_location")} placeholder={t("wform.p.location")} /></F>
              <F l={t("wform.l.reception")}><input className="editorial-input mt-2" value={form.reception_location} onChange={set("reception_location")} placeholder={t("wform.p.location")} /></F>
              <F l={t("wform.l.style")}><input className="editorial-input mt-2" value={form.wedding_style} onChange={set("wedding_style")} placeholder={t("wform.p.style")} /></F>
              <F l={t("wform.l.budget")}><input className="editorial-input mt-2" value={form.budget} onChange={set("budget")} placeholder={t("wform.p.optional")} /></F>
              <F l={t("wform.l.food")} full><input className="editorial-input mt-2" value={form.menu} onChange={set("menu")} placeholder={t("wform.p.food")} /></F>
              <F l={t("wform.l.dietary")} full><input className="editorial-input mt-2" value={form.dietary} onChange={set("dietary")} placeholder={t("wform.p.dietary")} /></F>

              <div className="sm:col-span-2">
                <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-[--text-2]">{t("wform.l.services")}</label>
                <div className="flex flex-wrap gap-2.5 mt-3">
                  {serviceOptions.map((s, i) => {
                    const on = form.services.includes(s);
                    return (
                      <button type="button" key={i} data-testid={`svc-${i}`} onClick={() => toggle(s)} className={`px-4 py-2 rounded-full border text-[11px] font-mono uppercase tracking-[0.1em] transition-colors duration-300 ${on ? "bg-[--gold] text-[#14180E] border-[--gold]" : "bg-transparent text-[--text-2] border-white/15 hover:border-[--gold]"}`}>{s}</button>
                    );
                  })}
                </div>
              </div>

              <F l={t("wform.l.info")} full><textarea rows={3} className="editorial-input mt-2 resize-none" value={form.additional_info} onChange={set("additional_info")} placeholder={t("wform.p.info")} /></F>

              <div className="sm:col-span-2 mt-1">
                <button data-testid="wedding-submit" type="submit" disabled={loading} className="w-full sm:w-auto inline-flex items-center justify-center gap-3 px-10 py-4 rounded-full bg-[--gold] text-[#14180E] font-mono text-[11px] uppercase tracking-[0.16em] hover:bg-[--text] transition-colors duration-400 disabled:opacity-60">
                  {loading ? <Loader2 className="animate-spin" size={16} /> : null}{loading ? t("wform.sending") : t("wform.submit")}
                </button>
              </div>
            </motion.form>
          )}
        </AnimatePresence>
      </div>
    </section>
  );
};

const F = ({ l, children, full }) => (
  <div className={full ? "sm:col-span-2" : "sm:col-span-1"}>
    <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-[--text-2]">{l}</label>
    {children}
  </div>
);
